import config from '@/config';
import { fetchWithKeycloakToken } from '@/features/Keycloak/keycloakService';
import type { TypedDocumentString } from '@/generated/graphql';
import {
  type GraphqlResponse,
  type GraphqlResponseError,
  handleGraphqlError,
} from './execute';

export class SenhubGraphqlNotConfiguredError extends Error {
  constructor() {
    super('The senhub GraphQL URL is not configured.');
    this.name = 'SenhubGraphqlNotConfiguredError';
  }
}

export function isSenhubGraphqlConfigured(): boolean {
  return !!config.senhubGraphql?.url;
}

// Calls senhub's own /graphql directly (not through Hasura), so variables
// never reach Hasura's query log.
export async function executeSenhub<
  TResult,
  TVariables extends object,
>(options: {
  query: TypedDocumentString<TResult, TVariables>;
  variables: TVariables;
}): Promise<TResult> {
  if (!isSenhubGraphqlConfigured()) {
    throw new SenhubGraphqlNotConfiguredError();
  }

  const response = await fetchWithKeycloakToken(config.senhubGraphql.url, {
    method: 'POST',
    headers: {
      Accept: 'application/graphql-response+json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: options.query,
      variables: options.variables,
    }),
  });

  if (!response.ok) {
    // HotChocolate answers validation failures with a 400 and a GraphQL body.
    const firstError = await readFirstGraphqlError(response);
    if (firstError) {
      await handleGraphqlError(firstError);
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const res = (await response.json()) as GraphqlResponse<TResult>;
  if (res.errors?.[0]) {
    await handleGraphqlError(res.errors[0]);
  }

  if (res.data === undefined) {
    throw new Error('GraphQL response missing data');
  }

  return res.data;
}

async function readFirstGraphqlError(
  response: Response,
): Promise<GraphqlResponseError | undefined> {
  try {
    const body = (await response.json()) as { errors?: unknown };
    const first: unknown = Array.isArray(body?.errors)
      ? body.errors[0]
      : undefined;
    if (
      typeof first === 'object' &&
      first !== null &&
      typeof (first as GraphqlResponseError).message === 'string'
    ) {
      return first as GraphqlResponseError;
    }
  } catch {
    // Not JSON.
  }
  return undefined;
}
